import { HeartBroken } from '@mui/icons-material'
import { ClickAwayListener } from '@mui/material'
import cn from 'classnames'
import React from 'react'
import { useTranslation } from 'react-i18next'

import Link from '../common/util/input/Link'
import { useSharedState } from '../waxplorer/Waxplorer'

const moreOptionsBtnClassName = cn(
    'flex justify-start w-32 transition delay-200 width',
    'h-5 rounded text-zinc-900 dark:!text-white cursor-pointer outline-none',
    'text-xs font-bold my-2',
)

const MoreOptionsButton = ({
    onClick,
    children,
    imgSrcDark,
    imgSrcLight,
    icon,
    imgAlt = typeof children === 'string' ? children : '',
}) => (
    <div className={moreOptionsBtnClassName} onClick={onClick}>
        <div className="w-5 h-5 mx-2 text-zinc-900 dark:!text-white">
            {imgSrcDark ? (
                <img
                    className="w-5 h-5 text-zinc-900 dark:!text-white hidden dark:!block"
                    src={imgSrcDark}
                    alt={imgAlt}
                />
            ) : (
                ''
            )}
            {imgSrcLight ? (
                <img
                    className="w-5 h-5 text-zinc-900 dark:!text-white block dark:!hidden"
                    src={imgSrcLight}
                    alt={imgAlt}
                />
            ) : (
                ''
            )}
            {icon ? icon : ''}
        </div>
        <div className="flex my-auto text-sm">{children}</div>
    </div>
)

const MoreOptionsAnchor = ({
    href,
    children,
    imgSrcLight,
    imgSrcDark,
    imgAlt,
}) => (
    <Link href={href} rel="noreferrer" external={href.includes('http')}>
        <MoreOptionsButton
            imgSrcLight={imgSrcLight}
            imgSrcDark={imgSrcDark}
            imgAlt={imgAlt}
            children={children}
        />
    </Link>
)

export const MoreOptions = ({
    asset,
    burned,
    created,
    newOwner,
    setIsLoading,
    setShowMenu,
    showMenu,
    sold,
    transferred,
    toggleShowMenu,
    currentAsset,
    setAction,
}) => {
    const { t } = useTranslation('common')
    const [state] = useSharedState()

    const activeUser = state?.activeUser

    const userName = activeUser?.accountName
        ? state?.activeUser.accountName
        : null

    if (newOwner) asset['owner'] = newOwner

    const { sender, template, author, unpackUrl, assets } = asset

    const { schema, assetId, contract, mdata, burnable, transferable, owner } =
        assets ? assets[currentAsset] : asset

    let isBreakable =
        mdata && mdata.includes('locked_assets') && mdata.includes('craft_id')

    const runAction = async (action) => {
        setIsLoading(true)
        setAction(action)
    }

    const auctionable =
        showMenu &&
        contract === 'atomicassets' &&
        owner === userName &&
        !sold &&
        assetId &&
        !transferred

    const isburnable =
        showMenu && burnable && !created && !transferred && userName === owner

    const unpackable =
        showMenu && unpackUrl && owner === userName && assetId && !burned

    const swappable =
        showMenu &&
        author === 'gpk.topps' &&
        ['series1', 'series2', 'exotic'].includes(schema) &&
        ['simpleassets', 'atomicassetes'].includes(contract) &&
        owner === userName &&
        !burned

    const isTransferable =
        showMenu &&
        !sold &&
        !transferred &&
        owner === userName &&
        assetId &&
        !burned &&
        (transferable || transferable === undefined)

    const buyable =
        showMenu &&
        !created &&
        userName &&
        template?.templateId &&
        template?.templateId > 0 &&
        !burned

    return (
        <ClickAwayListener onClickAway={() => setShowMenu(false)}>
            <div
                className={cn(
                    'h-auto absolute  p-2 z-20 w-40 right-0 top-0 flex-wrap',
                )}
            >
                <div
                    className={cn(
                        'h-auto absolute  p-2 z-20 w-40 right-0 top-0 flex-wrap',
                        'dark:!bg-paper bg-white rounded-xl',
                        { 'opacity-100 z-10 transition delay-200': showMenu },
                        {
                            'opacity-0 z-10 hidden transition delay-200':
                                !showMenu,
                        },
                    )}
                >
                    {isTransferable ? (
                        <MoreOptionsButton
                            onClick={() => runAction('transfer')}
                            imgSrcLight={
                                '/diagonal-arrow-right-up-outline-black.svg'
                            }
                            imgSrcDark={'/diagonal-arrow-right-up-outline.svg'}
                            imgAlt={t('asset.transfer')}
                        >
                            {t('asset.transfer')}
                        </MoreOptionsButton>
                    ) : null}
                    {swappable ? (
                        <MoreOptionsButton
                            onClick={() => runAction('swap')}
                            imgSrc={
                                contract === 'atomicassets'
                                    ? '/simpleassets.svg'
                                    : '/atomic.png'
                            }
                            imgAlt={t('profile.swap')}
                        >
                            {t('profile.swap')}
                        </MoreOptionsButton>
                    ) : null}
                    {auctionable ? (
                        <MoreOptionsButton
                            onClick={() => runAction('auction')}
                            imgSrcLight={'/pricetags-outline-white.svg'}
                            imgSrcDark={'/pricetags-outline.svg'}
                            imgAlt={t('popups.auction')}
                        >
                            {t('popups.auction')}
                        </MoreOptionsButton>
                    ) : null}
                    {unpackable ? (
                        <MoreOptionsAnchor
                            href={unpackUrl}
                            imgSrcDark={'/cube-outline.svg'}
                            imgSrcLight={'/cube-outline-black.svg'}
                            imgAlt={t('asset.unpack')}
                            onClick={() => setShowMenu(false)}
                        >
                            {t('asset.unpack')}
                        </MoreOptionsAnchor>
                    ) : null}
                    {buyable ? (
                        <MoreOptionsButton
                            imgSrcLight={'/pricetags-outline-white.svg'}
                            imgSrcDark={'/pricetags-outline.svg'}
                            onClick={() => runAction('create_buyoffer')}
                            imgAlt={t('Create Buy Offer')}
                        >
                            Buy Offer
                        </MoreOptionsButton>
                    ) : null}
                    {isburnable ? (
                        <MoreOptionsButton
                            imgSrcLight="/fire.png"
                            imgSrcDark="/fire.png"
                            onClick={() => runAction('burn_asset')}
                            imgAlt={t('asset.burn')}
                        >
                            {t('asset.burn')}
                        </MoreOptionsButton>
                    ) : null}
                    {isBreakable ? (
                        <MoreOptionsButton
                            icon={<HeartBroken fontSize="small" className="" />}
                            onClick={() => runAction('break_asset')}
                            imgAlt={t('asset.break')}
                        >
                            {t('asset.break')}
                        </MoreOptionsButton>
                    ) : null}
                </div>
                <div
                    onClick={toggleShowMenu}
                    className={cn(
                        'absolute text-sm w-8 h-8 right-0 top-0 text-zinc-900 dark:!text-white m-auto z-30',
                    )}
                >
                    <img
                        className={cn(
                            'absolute w-6 h-6 right-4 top-3 text-primary',
                            {
                                'transition transform duration-250 rotate-0':
                                    showMenu,
                            },
                            {
                                'transition transform duration-250 rotate-0':
                                    !showMenu,
                            },
                        )}
                        src="/more.svg"
                        alt="more"
                    />
                </div>
            </div>
        </ClickAwayListener>
    )
}

export default MoreOptions
